import type { RawTagNode, TagNode } from './types';

/**
 * 把接口返回的树按 DFS 前序洗成扁平行（父子相邻，便于按 level 缩进展示）。
 * id 可能重复或为空，故 key 用「父路径 / id # 兄弟序」合成；跨分支仍碰撞时追加全局序号兜底。
 */
export function flattenTree(roots: RawTagNode[]): TagNode[] {
  const out: TagNode[] = [];
  const usedKeys = new Set<string>();

  const walk = (nodes: RawTagNode[], level: number, parentId: string | null, parentPath: string[]) => {
    nodes.forEach((raw, i) => {
      const id = raw.id == null ? '' : String(raw.id);
      let key = `${parentId ?? 'root'}/${id || 'anon'}#${i}`;
      if (usedKeys.has(key)) key = `${key}@${out.length}`;
      usedKeys.add(key);

      const name = raw.node_name ?? '';
      const children = raw.children ?? [];
      const childParentId = id || key;

      out.push({
        key,
        id,
        name,
        level,
        parentId,
        parentPath,
        disabled: !!raw.disabled,
        hasChildren: children.length > 0,
        searchText: name.toLowerCase(),
      });

      if (children.length) walk(children, level + 1, childParentId, [...parentPath, childParentId]);
    });
  };

  walk(roots, 1, null, []);
  return out;
}

export interface VisibleResult {
  /** 要渲染的扁平行 */
  rows: TagNode[];
  /** 搜索命中的节点 id（高亮用） */
  matchedIds: Set<string>;
}

/**
 * 计算可见行：
 * - 浏览态（query 为空）：保留根节点 + 所有祖先都在 expanded 中的节点。
 * - 搜索态：命中节点 + 命中节点的整条祖先链全部可见（强制展开，忽略 expanded）。
 */
export function computeVisible(all: TagNode[], expanded: Set<string>, query: string): VisibleResult {
  const q = query.trim().toLowerCase();

  if (!q) {
    const rows = all.filter(n => n.parentId === null || n.parentPath.every(pid => expanded.has(pid)));
    return { rows, matchedIds: new Set() };
  }

  const byId = new Map(all.map(n => [n.id, n]));
  const matched = all.filter(n => n.searchText.includes(q));
  const matchedIds = new Set(matched.map(n => n.id));
  const visibleKeys = new Set<string>();
  for (const hit of matched) {
    visibleKeys.add(hit.key);
    for (const pid of hit.parentPath) {
      const ancestor = byId.get(pid);
      if (ancestor) visibleKeys.add(ancestor.key);
    }
  }

  return { rows: all.filter(n => visibleKeys.has(n.key)), matchedIds };
}
